import React, { useState, useRef, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Info } from 'lucide-react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import JobDetailsPopCard from './JobDetailsPopCard';
import ScheduleJobModal from '../scheduler/ScheduleJobModal';
import { avatarColor } from '../inbox/avatarUtils';
import { SLOT_HEIGHT } from './calendarConstants';
import { getDisplayName } from '../../lib/utils';

const STATUS_COLORS = {
  scheduled:   '#3B82F6',
  approved:    '#64748B',
  in_progress: '#EAB308',
  en_route:    '#F59E0B',
  on_site:     '#6366F1',
  completed:   '#22C55E',
};

const formatTimeRange = (start, end) => {
  const fmt = (d) => new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
};

const DRAG_THRESHOLD = 6; // pixels — minimum movement to initiate intentional drag

const WeekSchedulerEventCard = ({ task, dayIndex, style }) => {
  const [showPopCard, setShowPopCard] = useState(false);
  const [popCardPosition, setPopCardPosition] = useState({ top: 0, left: 0 });
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const dragStartPos = useRef(null);
  const hasDraggedBeyondThreshold = useRef(false);
  const cardRef = useRef(null);
  const isResizing = useRef(false);
  const [resizeDeltaSlots, setResizeDeltaSlots] = useState(0);

  const { updateTask } = useTasks();
  const { leads, updateLead } = useLeads();

  // Find the associated lead for this task
  const lead = leads?.find(l => l.id === task.leadId);

  // Derived display values — client name is always primary, service is secondary
  const clientName  = task.clientName || (lead ? getDisplayName(lead, '') : null) || task.title || 'Job';
  const serviceName = task.serviceType || lead?.service || '';
  const city = lead?.address?.city || lead?.clientInfo?.city || '';
  const statusColor = STATUS_COLORS[task.status] || STATUS_COLORS.scheduled;
  const timeRange = task.scheduledStart ? formatTimeRange(task.scheduledStart, task.scheduledEnd) : '';
  const baseCardHeight = parseInt(style?.height) || SLOT_HEIGHT;
  const cardHeight = Math.max(baseCardHeight + resizeDeltaSlots * SLOT_HEIGHT, SLOT_HEIGHT);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `job-${task.id}`,
    data: {
      type: 'job',
      taskId: task.id,
      scheduledStart: task.scheduledStart,
      scheduledEnd: task.scheduledEnd,
      dayIndex,
    },
  });

  // Handle pointer down - record start position
  const handlePointerDown = useCallback((e) => {
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    hasDraggedBeyondThreshold.current = false;
  }, []);

  // Handle pointer move - check if threshold exceeded
  const handlePointerMove = useCallback((e) => {
    if (!dragStartPos.current || hasDraggedBeyondThreshold.current) return;
    
    const dx = Math.abs(e.clientX - dragStartPos.current.x);
    const dy = Math.abs(e.clientY - dragStartPos.current.y);
    
    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
      hasDraggedBeyondThreshold.current = true;
    }
  }, []);

  // Handle pointer up - if no significant drag occurred, treat as click
  const handlePointerUp = useCallback((e) => {
    // Only open pop card if we didn't drag or resize
    if (!hasDraggedBeyondThreshold.current && dragStartPos.current && !isDragging && !isResizing.current) {
      const rect = cardRef.current?.getBoundingClientRect();
      if (rect) {
        setPopCardPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      } else {
        setPopCardPosition({
          top: e.clientY + 8,
          left: e.clientX,
        });
      }
      setShowPopCard(true);
    }
    dragStartPos.current = null;
  }, [isDragging]);

  // Handle info icon click (alternative entry point)
  const handleInfoClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const rect = cardRef.current?.getBoundingClientRect();
    if (rect) {
      setPopCardPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
    setShowPopCard(true);
  };

  // Handle unschedule from pop card
  const handleUnschedule = () => {
    // Clear schedule and mark task as approved (no slot assigned)
    updateTask(task.id, {
      scheduledStart: null,
      scheduledEnd: null,
      status: 'approved',
    });

    // Revert lead stage to Approved — job removed from calendar, back to awaiting scheduling
    if (task.leadId) {
      updateLead(task.leadId, { stage: 'Approved' });
    }
  };

  // Handle edit schedule — open ScheduleJobModal pre-filled with current values
  const handleEditSchedule = () => {
    setScheduleModalOpen(true);
  };

  // Compute initial modal values from the task's current schedule
  const getInitialScheduleValues = () => {
    if (!task.scheduledStart) return {};
    const start = new Date(task.scheduledStart);
    const end = task.scheduledEnd ? new Date(task.scheduledEnd) : null;
    const iDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
    const iTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    let iDuration = 60;
    if (end) {
      const diffMins = Math.round((end - start) / 60000);
      const durations = [60, 90, 120, 150, 180, 240];
      iDuration = durations.reduce((prev, curr) => Math.abs(curr - diffMins) < Math.abs(prev - diffMins) ? curr : prev);
    }
    return { initialDate: iDate, initialStartTime: iTime, initialDuration: iDuration };
  };

  // Handle resize — pointer drag on bottom handle snaps to 30-min slot grid
  const handleResizePointerDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    isResizing.current = true;

    const startY = e.clientY;
    const originalEndMs = task.scheduledEnd
      ? new Date(task.scheduledEnd).getTime()
      : new Date(task.scheduledStart).getTime() + 30 * 60 * 1000;
    const startMs = new Date(task.scheduledStart).getTime();
    const SLOT_MS = 30 * 60 * 1000;

    const onMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      setResizeDeltaSlots(Math.round(deltaY / SLOT_HEIGHT));
    };

    const onUp = (upEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);

      const deltaY = upEvent.clientY - startY;
      const slotsMoved = Math.round(deltaY / SLOT_HEIGHT);

      if (slotsMoved !== 0) {
        const newEndMs = originalEndMs + slotsMoved * SLOT_MS;
        const minEndMs = startMs + SLOT_MS; // minimum 1 slot = 30 min
        const clampedEndMs = Math.max(newEndMs, minEndMs);
        updateTask(task.id, {
          scheduledEnd: new Date(clampedEndMs).toISOString(),
        });
      }

      setResizeDeltaSlots(0);
      // Reset after event cycle so handlePointerUp cannot misfire
      setTimeout(() => { isResizing.current = false; }, 0);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [task.scheduledEnd, task.scheduledStart, task.id, updateTask]);

  const dragStyle = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.8 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      }
    : { cursor: 'grab' };

  // Technician color: light tint background + colored border
  const assignedName = task.assignedToName || '';
  const techColor = assignedName ? avatarColor(assignedName) : null;
  const cardBg = techColor ? `${techColor}1a` : `${statusColor}18`; // tint from tech color, or status color
  const cardBorderColor = techColor || statusColor;

  // Respect layout overrides passed by parent (e.g. collision-based width/left)
  const mergedStyle = {
    ...style,
    ...dragStyle,
    position: 'absolute',
    left:  style?.left  ?? '2px',
    right: style?.right ?? '2px',
    width: style?.width,            // undefined falls back to browser auto (honouring right:'2px')
    height: `${cardHeight}px`,      // live-updates during resize via resizeDeltaSlots
    zIndex: isDragging ? 100 : 10,
    pointerEvents: 'auto',
    backgroundColor: cardBg,
    borderLeft: `3px solid ${cardBorderColor}`,
    borderRadius: '6px',
    padding: '4px 6px',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.06)',
  };

  return (
    <>
      <div
        ref={(node) => {
          setNodeRef(node);
          cardRef.current = node;
        }}
        style={mergedStyle}
        {...attributes}
        {...listeners}
        onPointerDown={(e) => {
          handlePointerDown(e);
          listeners?.onPointerDown?.(e);
        }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={`
          group overflow-hidden box-border
          text-gray-800 select-none transition-shadow
          ${isDragging ? 'opacity-75' : ''}
        `}
        data-testid={`job-card-${task.id}`}
      >
        <div className="flex items-start justify-between gap-1">
          {/* Card content stack */}
          <div className="flex-1 overflow-hidden min-w-0">
            {/* Line 1: client name — always primary */}
            <div className="font-semibold truncate text-[11px] leading-snug text-gray-900">
              {clientName}
            </div>

            {/* Line 2: service / program name */}
            {cardHeight >= 50 && serviceName && (
              <div className="truncate text-[10px] leading-snug text-gray-600 mt-0.5">
                {serviceName}
              </div>
            )}

            {/* Line 3: time range */}
            {cardHeight >= 80 && timeRange && (
              <div className="truncate text-[10px] leading-snug text-gray-500 mt-0.5">
                {timeRange}
              </div>
            )}

            {/* Line 4: city (if enough height) */}
            {cardHeight >= 110 && city && (
              <div className="truncate text-[10px] leading-snug text-gray-400 mt-0.5">
                {city}
              </div>
            )}
          </div>

          {/* Info button */}
          <button
            type="button"
            onClick={handleInfoClick}
            onPointerDown={(e) => e.stopPropagation()}
            className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-300 hover:text-gray-500 transition-colors mt-0.5"
            title="View details"
            data-testid={`job-info-btn-${task.id}`}
          >
            <Info className="h-3 w-3" />
          </button>
        </div>

        {/* Resize handle — bottom edge drag to adjust duration */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 8,
            cursor: 'ns-resize',
            borderTop: resizeDeltaSlots !== 0
              ? '2px solid rgba(59,130,246,0.5)'
              : '2px dashed rgba(0,0,0,0.12)',
          }}
          onPointerDown={handleResizePointerDown}
          data-testid={`job-resize-handle-${task.id}`}
        />
      </div>

      {/* Job Details Pop Card */}
      <JobDetailsPopCard
        task={task}
        lead={lead}
        isOpen={showPopCard}
        onClose={() => setShowPopCard(false)}
        onUnschedule={handleUnschedule}
        onEditSchedule={handleEditSchedule}
        position={popCardPosition}
      />

      {/* Edit Schedule Modal */}
      {scheduleModalOpen && (
        <ScheduleJobModal
          lead={lead}
          isOpen={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          {...getInitialScheduleValues()}
        />
      )}
    </>
  );
};

export default WeekSchedulerEventCard;
